/**
 * Feb 2026 R8 · Requirement-brief template library page.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FileText, Sparkles } from "lucide-react";

export default function RequirementBriefTemplates() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [useDialog, setUseDialog] = useState(null);   // {template}
  const [instantTitle, setInstantTitle] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/requirement-brief-templates`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const useTemplate = async () => {
    if (!useDialog?.template) return;
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/requirement-briefs/from-template`,
        { template_id: useDialog.template.id,
          title: instantTitle.trim() || useDialog.template.name });
      toast.success("Draft created from template");
      window.location.href = `/app/requirement-briefs/${data.id}`;
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
  };

  return (
    <div className="min-h-screen" data-testid="rb-templates-page">
      <PageHeader
        title="Requirement brief templates"
        description="Reusable question sets — pick one, tweak it, and share in a click. Usage-ranked so your best-performing templates float up."
      />
      <div className="px-4 sm:px-6 md:px-10 py-6">
        {busy && rows.length === 0 && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!busy && rows.length === 0 && (
          <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="rb-templates-empty">
            No templates saved yet. Open any brief and click <b>Save as template</b> to add one here.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((t) => (
            <div key={t.id} className="border border-border p-3 flex flex-col" data-testid={`rb-tpl-${t.id}`}>
              <div className="flex items-start gap-2">
                <FileText className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{t.name}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {t.question_count} questions
                    {t.industry ? ` · ${t.industry}` : ""}
                    {t.usage_count > 0 ? ` · used ${t.usage_count}×` : " · not used yet"}
                  </div>
                </div>
              </div>
              {t.description && (
                <div className="text-xs text-muted-foreground mt-2 line-clamp-3">{t.description}</div>
              )}
              <div className="mt-3">
                <Button
                  size="sm" className="rounded-none w-full"
                  onClick={() => { setUseDialog({ template: t }); setInstantTitle(t.name); }}
                  data-testid={`rb-tpl-${t.id}-use`}
                >
                  <Sparkles className="w-3 h-3 mr-1" /> Use this template
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!useDialog} onOpenChange={(v) => !v && setUseDialog(null)}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader><DialogTitle>Use “{useDialog?.template?.name}”</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Give this brief a name (edit later if needed)</label>
            <Input value={instantTitle} onChange={(e) => setInstantTitle(e.target.value)}
                      className="rounded-none h-9" data-testid="rb-tpl-title" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setUseDialog(null)}>Cancel</Button>
            <Button className="rounded-none" onClick={useTemplate} data-testid="rb-tpl-use-confirm">Create draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
